if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('section');
        if (!this.section || this.closest('quick-add-modal')) return;

        this.sectionId = this.dataset.sectionId;
        this.mainButton = this.section.querySelector(`#product-form-${this.sectionId} [name="add"]`);
        this.stickyButton = this.querySelector('[data-sticky-atc-submit]');
        this.dropdown = this.querySelector('[data-sticky-atc-dropdown]');
        this.dropdownOptions = this.querySelector('[data-sticky-atc-dropdown-options]');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.available = this.dataset.available === 'true';
        this.variants = JSON.parse(this.querySelector('[data-sticky-atc-variants]')?.textContent || '[]');

        if (!this.mainButton || !this.stickyButton) return;

        this.onScroll = this.updateVisibility.bind(this);
        this.onVariantChange = this.handleVariantChange.bind(this);
        this.onMobileChange = this.updateVisibility.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.mobileQuery.addEventListener?.('change', this.onMobileChange);
        this.section.addEventListener('recollection:variant-change', this.onVariantChange);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.querySelectorAll('[data-sticky-atc-option]').forEach((button) => {
          button.addEventListener('click', () => this.toggleDropdown(button));
        });
        this.stickyButton.addEventListener('click', () => this.submitMainForm());

        this.buttonObserver = new MutationObserver(() => this.syncButtonState());
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        this.syncSelectedSwatch();
        this.syncButtonState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.mobileQuery?.removeEventListener?.('change', this.onMobileChange);
        this.section?.removeEventListener('recollection:variant-change', this.onVariantChange);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.buttonObserver?.disconnect();
      }

      updateVisibility() {
        if (!this.mainButton) return;
        const isPastMainButton = this.mainButton.getBoundingClientRect().bottom <= 0;
        this.hidden = !(this.mobileQuery.matches && this.available && isPastMainButton);
        if (this.hidden) this.closeDropdown();
      }

      handleVariantChange(event) {
        const variant = event.detail?.variant;
        this.available = Boolean(variant?.available);
        this.dataset.available = String(this.available);

        if (variant) {
          const variantDisplay = this.variants.find((item) => String(item.id) === String(variant.id));
          if (variantDisplay) this.updateVariantDisplay(variantDisplay);
        }

        this.syncSelectedSwatch();
        this.syncButtonState();
        if (!this.dropdown?.hidden && this.activeOptionButton) {
          this.openDropdown(this.activeOptionButton);
        }
        this.updateVisibility();
      }

      updateVariantDisplay(variant) {
        const colorPosition = Number(this.dataset.colorPosition || 0);
        const sizePosition = Number(this.dataset.sizePosition || 0);
        const size = this.querySelector('[data-sticky-atc-size]');
        const price = this.querySelector('[data-sticky-atc-price]');
        const comparePrice = this.querySelector('[data-sticky-atc-compare-price]');

        if (size && sizePosition) size.textContent = variant.options[sizePosition - 1] || '';
        if (price) price.textContent = variant.price;
        if (comparePrice) {
          comparePrice.textContent = variant.compareAtPrice || '';
          comparePrice.hidden = !variant.compareAtPrice;
        }

        if (colorPosition) this.dataset.colorValue = variant.options[colorPosition - 1] || '';
      }

      syncSelectedSwatch() {
        const target = this.querySelector('[data-sticky-atc-swatch]');
        const colorField = this.section?.querySelector('.product-form__input--color');
        const checked = colorField?.querySelector('input[type="radio"]:checked');
        const source = checked?.nextElementSibling?.querySelector('.option-variant-image, .option-color');
        if (!target) return;

        target.replaceChildren();
        if (source) target.appendChild(source.cloneNode(true));
      }

      syncButtonState() {
        if (!this.mainButton || !this.stickyButton) return;
        const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
        this.stickyButton.disabled = disabled;
        this.stickyButton.setAttribute('aria-disabled', String(disabled));
      }

      toggleDropdown(button) {
        if (!this.dropdown || !this.dropdownOptions) return;
        if (!this.dropdown.hidden && this.activeOptionButton === button) {
          this.closeDropdown();
          return;
        }
        this.openDropdown(button);
      }

      openDropdown(button) {
        const optionHandle = button.dataset.stickyAtcOption;
        const fieldset = this.section?.querySelector(`.product-form__input--${optionHandle}`);
        if (!fieldset) return;

        this.dropdownOptions.replaceChildren();
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = `product-sticky-atc__dropdown-option product-sticky-atc__dropdown-option--${optionHandle}`;
          optionButton.dataset.value = input.value;
          optionButton.classList.toggle('is-selected', input.checked);
          optionButton.classList.toggle('is-unavailable', input.disabled || input.classList.contains('disabled'));

          if (optionHandle === 'color') {
            const source = input.nextElementSibling?.querySelector('.option-variant-image, .option-color');
            const visual = document.createElement('span');
            visual.className = 'product-sticky-atc__dropdown-swatch';
            if (source) visual.appendChild(source.cloneNode(true));
            optionButton.appendChild(visual);
          }

          const value = document.createElement('span');
          value.textContent = input.value;
          optionButton.appendChild(value);
          optionButton.addEventListener('click', () => {
            input.click();
            this.closeDropdown();
          });
          this.dropdownOptions.appendChild(optionButton);
        });

        this.activeOptionButton?.setAttribute('aria-expanded', 'false');
        this.activeOptionButton = button;
        this.activeOptionButton.setAttribute('aria-expanded', 'true');
        this.dropdown.hidden = false;
      }

      closeDropdown() {
        if (!this.dropdown) return;
        this.dropdown.hidden = true;
        this.activeOptionButton?.setAttribute('aria-expanded', 'false');
        this.activeOptionButton = null;
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) this.closeDropdown();
      }

      handleDocumentKeydown(event) {
        if (event.key === 'Escape') this.closeDropdown();
      }

      submitMainForm() {
        if (!this.available || this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') return;
        this.mainButton.click();
      }
    }
  );
}
